import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Spinner, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faGlobe, faRefresh, faCheckCircle, faExclamationCircle, faClock } from '@fortawesome/free-solid-svg-icons';
import { ApiManager } from '../../../core/api-manager';
import { toast } from 'react-toastify';

const PerformanceTab = () => {
    const [loading, setLoading] = useState(true);
    const [serverInfo, setServerInfo] = useState(null);
    const [browserInfo, setBrowserInfo] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    useEffect(() => {
        loadServerData();
        getBrowserInfo();
    }, []);

    const loadServerData = async () => {
        setLoading(true);
        try {
            const startTime = performance.now();
            
            // Info server
            const serverResponse = await ApiManager.sendAuthenticatedRequest('/analytics/server_info');

            const endTime = performance.now();
            const responseTime = Math.round(endTime - startTime);

            setServerInfo({
                ...(serverResponse?.data || serverResponse),
                lastResponseTime: responseTime
            });
            setLastUpdate(new Date());
        } catch (error) {
            console.error('Error loading server data:', error);
            toast.error('Errore nel caricamento info server');
        } finally {
            setLoading(false);
        }
    };


    const getBrowserInfo = () => {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        setBrowserInfo({
            userAgent: navigator.userAgent,
            browser: getBrowserName(),
            platform: navigator.platform,
            language: navigator.language,
            cookieEnabled: navigator.cookieEnabled,
            onLine: navigator.onLine,
            connectionType: connection?.effectiveType || 'unknown',
            downlink: connection?.downlink || 'unknown',
            rtt: connection?.rtt || 'unknown'
        });
    };

    const getBrowserName = () => {
        const ua = navigator.userAgent;
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Safari')) return 'Safari';
        if (ua.includes('Edge')) return 'Edge';
        return 'Unknown';
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <>
            {/* Header con ultimo aggiornamento */}
            <Row className="g-3 mb-4">
                <Col lg={12}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5>Monitoraggio Server Real-time</h5>
                        <div className="d-flex align-items-center gap-3">
                            {lastUpdate && (
                                <small className="text-muted">
                                    <FontAwesomeIcon icon={faClock} className="me-1" />
                                    Ultimo aggiornamento: {lastUpdate.toLocaleTimeString()}
                                </small>
                            )}
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={loadServerData}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faRefresh} spin={loading} /> Aggiorna
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Info Server */}
            <Row className="g-3 mb-4">
                <Col lg={6}>
                    <Card>
                        <Card.Header>
                            <Card.Title>
                                <FontAwesomeIcon icon={faServer} className="me-2" />
                                Informazioni Server
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {serverInfo ? (
                                <Table size="sm" className="mb-0">
                                    <tbody>
                                        <tr>
                                            <td width="40%" className="fw-bold">PHP Version:</td>
                                            <td>{serverInfo.phpVersion || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">MySQL Version:</td>
                                            <td>{serverInfo.mysqlVersion || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Sistema Operativo:</td>
                                            <td>{serverInfo.osInfo || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Framework Version:</td>
                                            <td>{serverInfo.frameworkVersion || 'CKD v1.0'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Server Software:</td>
                                            <td>{serverInfo.serverSoftware || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Memoria Utilizzata:</td>
                                            <td>{serverInfo.memoryUsage || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Tempo Risposta:</td>
                                            <td>{serverInfo.lastResponseTime}ms</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-3">
                                    <FontAwesomeIcon icon={faExclamationCircle} className="text-warning mb-2" />
                                    <p className="mb-0">Dati server non disponibili</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                
                <Col lg={6}>
                    <Card>
                        <Card.Header>
                            <Card.Title>
                                <FontAwesomeIcon icon={faGlobe} className="me-2" />
                                Informazioni Browser
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {browserInfo ? (
                                <Table size="sm" className="mb-0">
                                    <tbody>
                                        <tr>
                                            <td width="40%" className="fw-bold">Browser:</td>
                                            <td>{browserInfo.browser}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Platform:</td>
                                            <td>{browserInfo.platform}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Lingua:</td>
                                            <td>{browserInfo.language}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Cookie Abilitati:</td>
                                            <td>
                                                <Badge bg={browserInfo.cookieEnabled ? 'success' : 'danger'}>
                                                    {browserInfo.cookieEnabled ? 'Sì' : 'No'}
                                                </Badge>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Stato Connessione:</td>
                                            <td>
                                                <Badge bg={browserInfo.onLine ? 'success' : 'danger'}>
                                                    <FontAwesomeIcon 
                                                        icon={browserInfo.onLine ? faCheckCircle : faExclamationCircle} 
                                                        className="me-1" 
                                                    />
                                                    {browserInfo.onLine ? 'Online' : 'Offline'}
                                                </Badge>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Tipo Connessione:</td>
                                            <td>{browserInfo.connectionType}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Velocità Download:</td>
                                            <td>{browserInfo.downlink} Mbps</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-3">
                                    <FontAwesomeIcon icon={faExclamationCircle} className="text-warning mb-2" />
                                    <p className="mb-0">Dati browser non disponibili</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

        </>
    );
};

export default PerformanceTab;